/**
 * Main App Routing
 * (TS Migrated)
 */

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import AppToastHost from './components/AppToastHost';
import AdminRoute from './components/AdminRoute';
import RequireTier from './components/RequireTier';
import RequireFlag from './components/RequireFlag';
import PageErrorBoundary from './components/PageErrorBoundary';
import NotificationHub from './components/NotificationHub';
import CookieConsent from './components/CookieConsent';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocalMountProvider } from './context/LocalMountContext';
import { useCapStatusFuelSync } from './hooks/useFuelBalance';
import './styles/App.css';
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const MainApp = lazy(() => import('./pages/MainApp'));
const Benchmark = lazy(() => import('./pages/Benchmark'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PaymentResult = lazy(() => import('./pages/PaymentResult'));
const Policy = lazy(() => import('./pages/Policy'));
const Docs = lazy(() => import('./pages/Docs'));
const CliHelp = lazy(() => import('./pages/CliHelp'));
const Updates = lazy(() => import('./pages/Updates'));
const Changelog = lazy(() => import('./pages/Changelog'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminOps = lazy(() => import('./pages/AdminOps'));
const AdminUtil = lazy(() => import('./pages/AdminUtil'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminConsole = lazy(() => import('./pages/AdminConsole'));
const BillingDashboard = lazy(() => import('./pages/BillingDashboard'));
const PlatformStats = lazy(() => import('./pages/PlatformStats'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const MDrivePage = lazy(() => import('./pages/MDrivePage'));
const SharedChatPage = lazy(() => import('./pages/SharedChatPage'));
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal'));
const SessionsTimeline = lazy(() => import('./pages/SessionsTimeline'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));
const LineageExplorer = lazy(() => import('./pages/LineageExplorer'));
const PipelineKanban = lazy(() => import('./pages/pipeline/PipelineKanban'));
const WebAuthnSettings = lazy(() => import('./pages/WebAuthnSettings'));
const DataManagement = lazy(() => import('./pages/DataManagement'));
// WIRE-CONNECTED-ACCOUNTS-ROUTE-01: Connected Accounts settings page (all OAuth-linked services)
const ConnectedAccounts = lazy(() => import('./pages/ConnectedAccounts'));
// ADMIN-DASHBOARD-01: Unified ops hub (health, live logs, users, billing, errors, infra, deploys)
const AdminOpsHub = lazy(() => import('./pages/AdminOpsHub'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Extensions = lazy(() => import('./pages/Extensions'));
const ExtensionDetail = lazy(() => import('./pages/ExtensionDetail'));

// /extension/:surface -> /extensions/:surface (singular alias)
const ExtensionAliasRedirect: React.FC = () => {
  const { surface } = useParams<{ surface: string }>();
  return <Navigate to={surface ? `/extensions/${encodeURIComponent(surface)}` : '/extensions'} replace />;
};

const AppRoutes: React.FC = () => {
  const { login, isLoading, sessionExpired } = useAuth();
  // Keep the canonical `user.fuel_credits` in sync with the live
  // CAP_STATUS_UPDATE SSE stream so every credit display reads one source.
  useCapStatusFuelSync();
  const hostname = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const docsHost = hostname === 'docs.msapling.com';

  if (docsHost) {
    return (
      <Suspense fallback={<div className="loading-screen">Loading MSapling...</div>}>
        <Routes>
          <Route path="*" element={<PageErrorBoundary pageName="Docs"><Docs /></PageErrorBoundary>} />
        </Routes>
      </Suspense>
    );
  }

  const fallback = <div className="loading-screen">Loading MSapling...</div>;

  // Session expired / no session AND no guest session could be established:
  // present a single clear sign-in wall instead of the half-broken
  // authenticated shell (which would 401 on every call and leak raw errors).
  // A valid guest session does NOT set sessionExpired, so anonymous guests
  // keep getting the full app — only the truly signed-out/expired case is
  // bounced to login.
  const appShell = (pageName: string, node: React.ReactNode): React.ReactNode => {
    if (isLoading) return fallback;
    if (sessionExpired) {
      return <PageErrorBoundary pageName="Login"><Login onLogin={(user) => login(user)} /></PageErrorBoundary>;
    }
    return <PageErrorBoundary pageName={pageName}>{node}</PageErrorBoundary>;
  };

  return (
    <Suspense fallback={fallback}>
      <Routes>
        <Route path="/admin/ops-hub" element={<PageErrorBoundary pageName="Admin Ops Hub"><AdminRoute><AdminOpsHub /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin/console" element={<PageErrorBoundary pageName="Admin Console"><AdminRoute><AdminConsole /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin/ops" element={<PageErrorBoundary pageName="Admin Ops"><AdminRoute><AdminOps /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin/util" element={<PageErrorBoundary pageName="Admin Util"><AdminRoute><AdminUtil /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin/reports" element={<PageErrorBoundary pageName="Admin Reports"><AdminRoute><AdminReports /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin" element={<PageErrorBoundary pageName="Admin Dashboard"><AdminRoute><AdminDashboard /></AdminRoute></PageErrorBoundary>} />
        <Route path="/admin/" element={<PageErrorBoundary pageName="Admin Dashboard"><AdminRoute><AdminDashboard /></AdminRoute></PageErrorBoundary>} />
        <Route path="/settings/security" element={<PageErrorBoundary pageName="Security Settings"><WebAuthnSettings /></PageErrorBoundary>} />
        <Route path="/settings/connected-accounts" element={<PageErrorBoundary pageName="Connected Accounts"><ConnectedAccounts /></PageErrorBoundary>} />
        <Route path="/account/data" element={<PageErrorBoundary pageName="Data Management"><DataManagement /></PageErrorBoundary>} />
        {/* MSAPLING-PLATFORM-STATS-DASHBOARD-20260824: personal usage dashboard
            locked behind free+sub (guests redirected to /pricing) -- previously
            had NO gate at all, just an in-component tier check that silently
            degraded rather than actually blocking access. */}
        <Route path="/billing" element={<PageErrorBoundary pageName="Billing"><RequireTier allow={['free', 'monthly', 'lifetime', 'infra', 'infra_credits', 'superadmin']}><BillingDashboard /></RequireTier></PageErrorBoundary>} />
        {/* Generic platform-wide dashboard: open to every logged-in tier
            including guests -- no tier gate here, that's the point of it. */}
        <Route path="/platform-stats" element={<PageErrorBoundary pageName="PlatformStats"><PlatformStats /></PageErrorBoundary>} />
        {/* Per-user "your work/tokens/usage/telemetry" dashboard -- open to
            EVERY tier including guest (unlike /billing, which is gated to
            free+paid). Pulls the same GET /telemetry/user/usage endpoint
            already used elsewhere; no tier gate here is the whole point. */}
        <Route path="/dashboard" element={<PageErrorBoundary pageName="UserDashboard"><UserDashboard /></PageErrorBoundary>} />
        <Route path="/sessions" element={<PageErrorBoundary pageName="Session Timeline"><SessionsTimeline /></PageErrorBoundary>} />
        <Route path="/developer" element={<PageErrorBoundary pageName="Developer Portal"><DeveloperPortal /></PageErrorBoundary>} />
        <Route path="/mdrive" element={<PageErrorBoundary pageName="MDrive"><MDrivePage /></PageErrorBoundary>} />
        <Route path="/benchmark" element={<RequireFlag flag="benchmark_mode"><PageErrorBoundary pageName="Benchmark"><Benchmark /></PageErrorBoundary></RequireFlag>} />
        <Route path="/pricing" element={<PageErrorBoundary pageName="Pricing"><Pricing /></PageErrorBoundary>} />
        <Route path="/docs" element={<PageErrorBoundary pageName="Docs"><Docs /></PageErrorBoundary>} />
        <Route path="/extensions" element={<PageErrorBoundary pageName="Extensions"><Extensions /></PageErrorBoundary>} />
        <Route path="/extensions/:surface" element={<PageErrorBoundary pageName="Extension"><ExtensionDetail /></PageErrorBoundary>} />
        <Route path="/extension" element={<Navigate to="/extensions" replace />} />
        <Route path="/extension/:surface" element={<ExtensionAliasRedirect />} />
        <Route path="/cli/help" element={<PageErrorBoundary pageName="CLI Help"><CliHelp /></PageErrorBoundary>} />
        <Route path="/updates" element={<PageErrorBoundary pageName="Updates"><Updates /></PageErrorBoundary>} />
        <Route path="/changelog" element={<PageErrorBoundary pageName="Changelog"><Changelog /></PageErrorBoundary>} />
        <Route path="/policy" element={<PageErrorBoundary pageName="Policy"><Policy /></PageErrorBoundary>} />
        <Route path="/verify-email" element={<PageErrorBoundary pageName="Verify Email"><VerifyEmail /></PageErrorBoundary>} />
        <Route path="/reset-password" element={<PageErrorBoundary pageName="Reset Password"><ResetPassword /></PageErrorBoundary>} />
        <Route path="/payment-result" element={<PageErrorBoundary pageName="Payment Result"><PaymentResult /></PageErrorBoundary>} />
        <Route path="/payment/success" element={<PageErrorBoundary pageName="Payment Result"><PaymentResult /></PageErrorBoundary>} />
        <Route path="/payment/cancel" element={<PageErrorBoundary pageName="Payment Result"><PaymentResult /></PageErrorBoundary>} />
        <Route path="/waitlist" element={<PageErrorBoundary pageName="Waitlist"><WaitlistPage /></PageErrorBoundary>} />
        <Route path="/status" element={<PageErrorBoundary pageName="Status"><StatusPage /></PageErrorBoundary>} />
        <Route path="/lineage" element={<PageErrorBoundary pageName="Lineage Explorer"><AdminRoute><LineageExplorer /></AdminRoute></PageErrorBoundary>} />
        <Route path="/pipeline" element={<PageErrorBoundary pageName="Pipeline Kanban"><AdminRoute><PipelineKanban /></AdminRoute></PageErrorBoundary>} />
        {/* Deep link routes */}
        <Route path="/chat/:chatId" element={appShell('MSapling', <MainApp />)} />
        <Route path="/project/:projectName" element={appShell('MSapling', <MainApp />)} />
        <Route path="/shared/:token" element={<PageErrorBoundary pageName="Shared Chat"><SharedChatPage /></PageErrorBoundary>} />
        <Route path="/login" element={<PageErrorBoundary pageName="Login"><Login onLogin={(user) => login(user)} /></PageErrorBoundary>} />
        <Route path="/register" element={<PageErrorBoundary pageName="Register"><Register onLogin={(user) => login(user)} /></PageErrorBoundary>} />
        <Route path="/" element={appShell('MSapling', <MainApp />)} />
        <Route path="*" element={<PageErrorBoundary pageName="Not Found"><NotFound /></PageErrorBoundary>} />
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppToastHost />
        <NotificationHub />
        <LocalMountProvider>
          <AppRoutes />
        </LocalMountProvider>
        <CookieConsent />
      </AuthProvider>
    </Router>
  );
};

export default App;
